import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Bot, SendHorizonal, X, Upload, Trash2, FileText, Loader2, AlertCircle, CheckCircle2, Download } from 'lucide-react';
import { Card } from '../ui';
import { PageHeader } from '../components/PageHeader';
import {
  fetchIntelligence,
  acceptBooking,
  rejectBooking,
  sendChatMessage,
  uploadCaseDocument,
  fetchCaseDocuments,
  deleteCaseDocument,
  downloadCaseDocument,
} from '../services/caseIntelligenceService';
// ─── Quick-action prompts shown in the initial chat card ──────────────────────
const CASE_BOT_MENU = [
  { id: 'brief',     label: 'Case Brief',   icon: '📂', prompt: 'Give me a case brief summary.' },
  { id: 'checklist', label: 'Checklist',    icon: '📋', prompt: 'Show me the intake checklist.' },
  { id: 'risks',     label: 'Key Risks',    icon: '⚠️',  prompt: 'What are the key legal risks in this case?' },
  { id: 'strategy',  label: 'Strategy',     icon: '🧠', prompt: 'Suggest a legal strategy for this case.' },
];
// ─── Priority badge colours ───────────────────────────────────────────────────
const PRIORITY_STYLES = {
  high:   'bg-red-100 text-red-700 border border-red-200',
  medium: 'bg-amber-100 text-amber-700 border border-amber-200',
  low:    'bg-slate-100 text-slate-600 border border-slate-200',
};
// ─── File size formatter ──────────────────────────────────────────────────────
function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
// ─── Loading spinner ──────────────────────────────────────────────────────────
function Spinner({ size = 16, className = '' }) {
  return <Loader2 size={size} className={`animate-spin ${className}`} />;
}
// ─── Main component ───────────────────────────────────────────────────────────
export const AICaseIntelligencePage = (props) => {
  console.log("ACTIVE AI FILE:", typeof __filename !== 'undefined' ? __filename : import.meta?.url);
  const { onNavigate, navState } = props;
  const caseData   = navState?.caseData  || {};
  // Resolve bookingId from every possible navState shape lawyers might pass.
  // Priority: explicit bookingId field > caseId > id > top-level navState.bookingId
  const bookingId  = String(
    caseData?.bookingId ||
    caseData?.caseId    ||
    caseData?.id        ||
    navState?.bookingId ||   // fallback: some nav calls pass it at top level
    ''
  ).trim();
  const clientName = String(caseData?.name || caseData?.clientName || 'Client').trim();
  const caseType   = String(caseData?.type || caseData?.caseType  || 'Legal Consultation').trim();
  const location   = String(caseData?.location || 'India').trim();
  // TRACE LOGS — remove once root cause is confirmed
  console.log("RENDER bookingId value:", bookingId);
  console.log('[AICasePage] RENDER — navState keys:', navState ? Object.keys(navState) : 'null');
  console.log('[AICasePage] RENDER — caseData:', caseData);
  console.log('[AICasePage] RENDER — resolved bookingId:', JSON.stringify(bookingId));
  // ─── Intelligence state ───────────────────────────────────────────────────
  const [intelligence,        setIntelligence]        = useState(null);
  const [intelligenceLoading, setIntelligenceLoading] = useState(true);
  const [intelligenceError,   setIntelligenceError]   = useState(null);
  // ─── Case acceptance state ────────────────────────────────────────────────
  const [caseStatus,    setCaseStatus]    = useState(caseData?.status || null);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [acceptError,   setAcceptError]   = useState(null);
  // ─── Chat state ───────────────────────────────────────────────────────────
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput,    setChatInput]    = useState('');
  const [chatLoading,  setChatLoading]  = useState(false);
  const messagesEndRef                  = useRef(null);
  // ─── Document state ───────────────────────────────────────────────────────
  const [documents,       setDocuments]       = useState([]);
  const [docsLoading,     setDocsLoading]     = useState(false);
  const [uploadLoading,   setUploadLoading]   = useState(false);
  const [deletingDocId,   setDeletingDocId]   = useState(null);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const [docError,        setDocError]        = useState(null);
  const fileInputRef                          = useRef(null);
  // ─── Load intelligence ────────────────────────────────────────────────────
  const loadIntelligence = useCallback(async () => {
    console.log("🔥 BEFORE fetchIntelligence() with bookingId:", bookingId);
    console.log("🔥 Exact URL requested: http://localhost:5000/api/ai/intelligence/" + encodeURIComponent(bookingId));
    if (!bookingId) {
      console.warn('[AICasePage][loadIntelligence] EARLY EXIT — bookingId is empty. intelligenceLoading stays true.');
      return;
    }
    setIntelligenceLoading(true);
    setIntelligenceError(null);
    try {
      const data = await fetchIntelligence(bookingId);
      console.log("🔥 AFTER fetchIntelligence() returned");
      console.log("🔥 Response payload:", JSON.stringify(data));
      setIntelligence(data);
    } catch (err) {
      console.log("🔥 CATCH block error:", err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to load intelligence.';
      console.error('[AICasePage][loadIntelligence] ERROR:', err?.response?.status, msg, err);
      setIntelligenceError(msg);
    } finally {
      setIntelligenceLoading(false);
    }
  }, [bookingId]);
  // ─── Load documents ───────────────────────────────────────────────────────
  const loadDocuments = useCallback(async () => {
    console.log('[AICasePage][loadDocuments] called — bookingId:', JSON.stringify(bookingId));
    if (!bookingId) return;
    setDocsLoading(true);
    try {
      const data = await fetchCaseDocuments(bookingId);
      setDocuments(Array.isArray(data?.documents) ? data.documents : []);
    } catch (err) {
      console.warn('[AICasePage][loadDocuments] error (non-critical):', err?.message);
    } finally {
      setDocsLoading(false);
    }
  }, [bookingId]);
  useEffect(() => {
    console.log("🔥 AICaseIntelligencePage MOUNTED");
  }, []);
  useEffect(() => {
    console.log("🔥 useEffect executing with bookingId:", bookingId);
    if (bookingId) {
      loadIntelligence();
      loadDocuments();
    }
  }, [bookingId, loadIntelligence, loadDocuments]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);
  // ─── Accept / Reject ──────────────────────────────────────────────────────
  const handleAccept = async () => {
    if (!bookingId || acceptLoading) return;
    setAcceptLoading(true);
    setAcceptError(null);
    try {
      await acceptBooking(bookingId);
      setCaseStatus('accepted');
    } catch (err) {
      setAcceptError(err?.response?.data?.error || err?.message || 'Failed to accept case.');
    } finally {
      setAcceptLoading(false);
    }
  };
  const handleReject = async () => {
    if (!bookingId || acceptLoading) return;
    setAcceptLoading(true);
    setAcceptError(null);
    try {
      await rejectBooking(bookingId);
      onNavigate('cases');
    } catch (err) {
      setAcceptError(err?.response?.data?.error || err?.message || 'Failed to decline case.');
      setAcceptLoading(false);
    }
  };
  // ─── Chat ─────────────────────────────────────────────────────────────────
  const handleChat = async (message) => {
    const text = String(message || '').trim();
    if (!text || chatLoading || !bookingId) return;
    setChatMessages((prev) => [
      ...prev,
      { id: Date.now(), role: 'user', text },
    ]);
    setChatInput('');
    setChatLoading(true);
    try {
      const { reply } = await sendChatMessage(bookingId, text);
      setChatMessages((prev) => [
        ...prev,
        { id: Date.now(), role: 'assistant', content: String(reply || 'No response received.') },
      ]);
    } catch (err) {
      setChatMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          role: 'assistant',
          content: err?.response?.data?.message || 'AI service temporarily unavailable. Please try again.',
          isError: true,
        },
      ]);
    } finally {
      setChatLoading(false);
    }
  };
  // ─── Document upload ──────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !bookingId) return;
    e.target.value = '';
    setUploadLoading(true);
    setDocError(null);
    for (const file of files) {
      try {
        await uploadCaseDocument(bookingId, file);
      } catch (err) {
        setDocError(
          err?.response?.data?.message || err?.message || `Failed to upload ${file.name}.`
        );
      }
    }
    await loadDocuments();
    setUploadLoading(false);
  };
  const handleDeleteDoc = async (docId) => {
    if (!bookingId || deletingDocId) return;
    setDeletingDocId(docId);
    setDocError(null);
    try {
      await deleteCaseDocument(bookingId, docId);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (err) {
      setDocError(err?.response?.data?.message || 'Failed to delete document.');
    } finally {
      setDeletingDocId(null);
    }
  };
  const handleDownloadDoc = async (doc) => {
    if (!bookingId || downloadingDocId) return;
    setDownloadingDocId(doc.id);
    setDocError(null);
    try {
      await downloadCaseDocument(bookingId, doc.id, doc.file_name);
    } catch (err) {
      setDocError(err?.response?.data?.message || 'Failed to download document.');
    } finally {
      setDownloadingDocId(null);
    }
  };
  // ─── Render helpers ───────────────────────────────────────────────────────
  const renderIntelligencePanel = () => {
    if (intelligenceLoading) {
      return (
        <div className="flex items-center gap-2 text-slate-500 py-4">
          <Spinner size={18} /> <span className="text-sm">Loading AI intelligence...</span>
        </div>
      );
    }
    if (intelligenceError) {
      return (
        <div className="flex items-start gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl p-3 text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{intelligenceError}</span>
        </div>
      );
    }
    if (!intelligence?.hasIntelligence) {
      return (
        <div className="text-sm text-slate-500 bg-slate-50 rounded-xl p-4">
          No AI intelligence available yet. This booking has not been processed through the AI intake pipeline.
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {/* Summary */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Case Summary</p>
          <p className="text-sm text-slate-700 leading-relaxed bg-slate-50 rounded-xl p-3">
            {intelligence.summary || 'No summary available.'}
          </p>
        </div>
        {/* Checklist */}
        {Array.isArray(intelligence.checklist) && intelligence.checklist.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Intake Checklist</p>
            <ul className="space-y-1">
              {intelligence.checklist.map((item, i) => (
                <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Insights */}
        {Array.isArray(intelligence.insights) && intelligence.insights.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">AI Insights</p>
            <div className="space-y-2">
              {intelligence.insights.map((insight, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-slate-800">{insight.title}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${PRIORITY_STYLES[insight.priority] || PRIORITY_STYLES.low}`}>
                      {insight.priority}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{insight.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Drafts */}
        {Array.isArray(intelligence.drafts) && intelligence.drafts.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Suggested Drafts</p>
            <div className="flex flex-wrap gap-2">
              {intelligence.drafts.map((draft, i) => (
                <span key={i} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700 font-medium">
                  {draft}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };
  const renderDocuments = () => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Documents</p>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadLoading}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
        >
          {uploadLoading ? <Spinner size={12} /> : <Upload size={12} />}
          {uploadLoading ? 'Uploading...' : 'Upload'}
        </button>
      </div>
      <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileChange}
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt" />
      {docError && (
        <p className="text-xs text-red-600 mb-2">{docError}</p>
      )}
      {docsLoading ? (
        <div className="flex items-center gap-2 text-slate-400 text-xs py-2">
          <Spinner size={12} /> Loading documents...
        </div>
      ) : documents.length === 0 ? (
        <p className="text-xs text-slate-400">No documents uploaded yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {documents.map((doc) => (
            <li key={doc.id} className="flex items-center gap-2 text-sm text-slate-700 group">
              <FileText size={14} className="text-slate-400 shrink-0" />
              <span className="flex-1 truncate" title={doc.file_name}>{doc.file_name}</span>
              {doc.file_size ? (
                <span className="text-[10px] text-slate-400 shrink-0">{formatBytes(doc.file_size)}</span>
              ) : null}
              {/* Download */}
              <button
                onClick={() => handleDownloadDoc(doc)}
                disabled={downloadingDocId === doc.id || !!deletingDocId}
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-700 transition-all disabled:opacity-50"
                title="Download document"
              >
                {downloadingDocId === doc.id ? <Spinner size={12} /> : <Download size={12} />}
              </button>
              {/* Delete */}
              <button
                onClick={() => handleDeleteDoc(doc.id)}
                disabled={deletingDocId === doc.id || !!downloadingDocId}
                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all disabled:opacity-50"
                title="Delete document"
              >
                {deletingDocId === doc.id ? <Spinner size={12} /> : <Trash2 size={12} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  // ─── Page render ──────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="AI Case Intelligence"
        subtitle={`${clientName} · ${caseType} · ${location}`}
        showBack
        onBack={() => onNavigate('cases')}
      />
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* ─── Left panel ─── */}
        <div className="xl:col-span-4 space-y-4">
          {/* Case card */}
          <Card className="p-0">
            <div className="p-6">
              <h3 className="text-2xl font-bold text-slate-900">{clientName}</h3>
              <p className="text-slate-500 text-sm mt-0.5">{caseType} · {location}</p>
              {/* Intelligence status badge */}
              {!intelligenceLoading && intelligence && (
                <div className={`mt-3 rounded-xl px-4 py-3 text-sm font-medium ${
                  intelligence.hasIntelligence
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                    : 'bg-slate-50 border border-slate-200 text-slate-600'
                }`}>
                  {intelligence.hasIntelligence
                    ? `✅ AI review complete · ${intelligence.qualityLabel} confidence (${Math.round((intelligence.confidence || 0) * 100)}%)`
                    : '⏳ No AI intake data yet'}
                </div>
              )}
              {/* Accept / Reject */}
              {acceptError && (
                <p className="mt-2 text-xs text-red-600">{acceptError}</p>
              )}
              {caseStatus === 'accepted' ? (
                <div className="mt-4 py-2.5 px-3 rounded-xl bg-emerald-100 border border-emerald-300 text-emerald-700 text-sm font-semibold text-center flex items-center justify-center gap-2">
                  <CheckCircle2 size={16} /> Case Accepted
                </div>
              ) : caseStatus === 'rejected' ? (
                <div className="mt-4 py-2.5 px-3 rounded-xl bg-red-100 border border-red-300 text-red-700 text-sm font-semibold text-center">
                  Case Declined
                </div>
              ) : (
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={handleAccept}
                    disabled={acceptLoading || !bookingId}
                    className="flex-1 py-3 rounded-xl bg-emerald-500 text-white font-semibold text-sm hover:bg-emerald-600 disabled:opacity-60 flex items-center justify-center gap-2 transition-colors"
                  >
                    {acceptLoading ? <Spinner size={14} className="text-white" /> : null}
                    Accept
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={acceptLoading || !bookingId}
                    className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-500 font-semibold text-sm hover:bg-slate-50 disabled:opacity-60 transition-colors"
                  >
                    Decline
                  </button>
                </div>
              )}
              {!bookingId && (
                <p className="mt-3 text-xs text-amber-600">
                  ⚠ No booking ID found. Open this page from the Cases list.
                </p>
              )}
            </div>
          </Card>
          {/* Documents panel */}
          <Card className="p-0">
            <div className="p-5">
              {renderDocuments()}
            </div>
          </Card>
          {/* Quick Actions */}
          <Card className="p-0">
            <div className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Quick Actions</p>
              <div className="space-y-2">
                {[
                  { label: '📂 Case Brief',     onClick: () => handleChat('Give me a case brief summary.') },
                  { label: '🧠 AI Intelligence', onClick: () => handleChat('What are the key legal risks in this case?') },
                  { label: '📝 Draft Notice',    onClick: () => handleChat('Help me draft a legal notice for this case.') },
                ].map((action) => (
                  <button
                    key={action.label}
                    onClick={action.onClick}
                    disabled={chatLoading || !bookingId}
                    className="w-full py-3 px-3 rounded-lg border border-slate-300 text-left text-sm font-semibold text-slate-800 hover:bg-black hover:text-white hover:border-black transition-colors disabled:opacity-50"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          </Card>
        </div>
        {/* ─── Right panel: Chat + Intelligence ─── */}
        <div className="xl:col-span-8 space-y-4">
          {/* Intelligence panel */}
          <Card className="p-0">
            <div className="p-5 border-b border-slate-100">
              <p className="text-base font-bold text-slate-800">AI Intelligence Report</p>
            </div>
            <div className="p-5">
              {renderIntelligencePanel()}
            </div>
          </Card>
          {/* Chat panel */}
          <Card className="p-0 flex flex-col min-h-[420px] overflow-hidden border border-slate-200 shadow-none bg-white">
            {/* Chat header */}
            <div className="px-4 py-3 bg-white border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="relative h-10 w-10 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-600">
                  <Bot size={18} />
                  <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-lime-500 border border-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800 leading-none">Case Bot</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {chatLoading ? 'Thinking...' : 'Online · Powered by AI'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setChatMessages([])}
                className="h-8 w-8 rounded-lg text-slate-400 hover:bg-slate-100 flex items-center justify-center transition-colors"
                title="Clear chat"
              >
                <X size={16} />
              </button>
            </div>
            {/* Messages */}
            <div className="flex-1 p-4 bg-slate-50 overflow-y-auto">
              {/* Welcome card with quick actions */}
              <div className="flex items-start gap-2.5 mb-4">
                <div className="h-7 w-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 shrink-0">
                  <Bot size={14} />
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-slate-600 mb-1.5">Case Bot</p>
                  <div className="w-full max-w-[320px] rounded-xl border border-slate-200 bg-white overflow-hidden">
                    <div className="px-3 py-3 text-sm leading-6 font-medium text-slate-800 border-b border-slate-200">
                      👋 Hi! I'm your AI legal assistant for {clientName}'s case. I can brief you on case facts, highlight risks, suggest strategy, and help draft documents.
                    </div>
                    <div className="divide-y divide-slate-100">
                      {CASE_BOT_MENU.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleChat(item.prompt)}
                          disabled={chatLoading || !bookingId}
                          className="w-full px-3 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                        >
                          <span className="mr-2" aria-hidden="true">{item.icon}</span>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {/* Conversation */}
              {chatMessages.map((msg) => {
                const isUser = msg.role === 'user';
                return (
                  <div key={msg.id} className={`mb-4 flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    {isUser ? (
                      <div className="max-w-[75%] rounded-xl rounded-tr-sm bg-amber-300 text-slate-900 px-3.5 py-2 text-sm font-medium border border-amber-400/40">
                        {msg.text}
                      </div>
                    ) : (
                      <div className="flex items-start gap-2.5">
                        <div className="h-7 w-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 shrink-0">
                          <Bot size={14} />
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold text-slate-600 mb-1">Case Bot</p>
                          <div className={`max-w-[340px] bg-white border text-slate-700 rounded-xl rounded-tl-sm px-3 py-2.5 text-sm leading-6 whitespace-pre-line ${
                            msg.isError ? 'border-red-200 bg-red-50 text-red-700' : 'border-slate-200'
                          }`}>
                            {msg.content}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {/* AI thinking indicator */}
              {chatLoading && (
                <div className="flex items-start gap-2.5 mb-4">
                  <div className="h-7 w-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 shrink-0">
                    <Bot size={14} />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-slate-600 mb-1">Case Bot</p>
                    <div className="bg-white border border-slate-200 rounded-xl rounded-tl-sm px-4 py-3 flex items-center gap-2 text-slate-400 text-sm">
                      <Spinner size={14} /> Thinking...
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            {/* Input */}
            <div className="p-2.5 border-t border-slate-200 bg-white">
              <div className="flex items-center gap-2">
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleChat(chatInput);
                    }
                  }}
                  placeholder={bookingId ? 'Ask about this case...' : 'Open this page from Cases to enable chat.'}
                  disabled={chatLoading || !bookingId}
                  className="flex-1 h-10 px-3.5 rounded-lg border border-slate-200 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-50"
                />
                <button
                  onClick={() => handleChat(chatInput)}
                  disabled={chatLoading || !chatInput.trim() || !bookingId}
                  className="h-10 w-10 rounded-lg bg-slate-900 text-white hover:bg-slate-700 flex items-center justify-center transition-colors disabled:opacity-40"
                >
                  {chatLoading ? <Spinner size={14} className="text-white" /> : <SendHorizonal size={16} />}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
